import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, RATINGS, ratingMeta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wind, ArrowLeft, Plus, Trash2, Pencil, MapPin, Upload, ImageIcon, X, Quote, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const emptySpot = { name: "", latitude: "", longitude: "", description: "", image_url: "", webcam_url: "", windguru_id: "" };

export default function Admin() {
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const [updates, setUpdates] = useState([]);

  const load = async () => {
    const [s, u] = await Promise.all([api.get("/spots"), api.get("/updates")]);
    setSpots(s.data);
    setUpdates(u.data);
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-sky-50">
      <div className="glass border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-700 hover:text-sky-600" data-testid="admin-back">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-head font-bold">Pannello Seba</span>
          </Link>
          <span className="text-sm text-slate-500">{user?.name || "Seba"}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="post">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-auto">
            <TabsTrigger value="post" className="rounded-full px-5 py-2 data-[state=active]:bg-sky-600 data-[state=active]:text-white font-head font-semibold" data-testid="tab-post">
              Nuovo aggiornamento
            </TabsTrigger>
            <TabsTrigger value="spots" className="rounded-full px-5 py-2 data-[state=active]:bg-sky-600 data-[state=active]:text-white font-head font-semibold" data-testid="tab-spots">
              Gestisci spot
            </TabsTrigger>
          </TabsList>

          <TabsContent value="post" className="mt-6 space-y-6">
            <VerdictManager />
            <PhraseManager />
            <UpdateForm spots={spots} onDone={load} />
            <UpdatesList updates={updates} onDone={load} />
          </TabsContent>

          <TabsContent value="spots" className="mt-6">
            <SpotManager spots={spots} onDone={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function VerdictManager() {
  const [sizes, setSizes] = useState([]);
  const [verdict, setVerdict] = useState("");
  const [sailKid, setSailKid] = useState("");
  const [sailAdult, setSailAdult] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/verdict").then((r) => {
      setSizes(r.data.sail_sizes || []);
      setVerdict(r.data.verdict || "");
      setSailKid(r.data.sail_kid || "");
      setSailAdult(r.data.sail_adult || "");
    }).catch(() => {});
  }, []);

  const save = async (chosenVerdict) => {
    const v = chosenVerdict ?? verdict;
    if (!v || v === "auto") {
      toast.error("Scegli prima il verdetto");
      return;
    }
    setSaving(true);
    try {
      await api.post("/verdict", { verdict: v, sail_kid: sailKid, sail_adult: sailAdult });
      setVerdict(v);
      toast.success("Verdetto pubblicato!");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const SailRow = ({ label, value, onChange }) => (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {sizes.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(value === s ? "" : s)}
            data-testid={`sail-${label.toLowerCase()}-${s}`}
            className={`font-head font-bold text-sm rounded-xl px-4 py-2 border transition-colors duration-200 ${
              value === s ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-5" data-testid="verdict-manager">
      <div className="flex items-center gap-2">
        <img src="/sail.png" alt="vela" className="h-6 w-6 object-contain" />
        <h2 className="font-head text-xl font-bold text-slate-900">Verdetto di oggi</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setVerdict("worth")}
          data-testid="verdict-worth"
          className={`flex items-center gap-3 rounded-2xl px-5 py-4 border-2 transition-all duration-200 ${
            verdict === "worth" ? "bg-emerald-500 border-emerald-500 text-white shadow-md" : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          }`}
        >
          <CheckCircle2 className="h-7 w-7 shrink-0" />
          <span className="font-head text-lg font-extrabold text-left">VALE LA PENA</span>
        </button>
        <button
          type="button"
          onClick={() => setVerdict("sleep")}
          data-testid="verdict-sleep"
          className={`flex items-center gap-3 rounded-2xl px-5 py-4 border-2 transition-all duration-200 ${
            verdict === "sleep" ? "bg-red-600 border-red-600 text-white shadow-md" : "bg-white border-red-200 text-red-600 hover:bg-red-50"
          }`}
        >
          <XCircle className="h-7 w-7 shrink-0" />
          <span className="font-head text-base font-extrabold text-left leading-tight">È na merda, tornate a dormire</span>
        </button>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Scegli tu il verdetto di oggi e le vele consigliate, poi salva 👇
      </p>

      <div className="pt-1">
        <div className="flex items-center gap-2 mb-3">
          <img src="/sail.png" alt="vela" className="h-5 w-5 object-contain" />
          <h3 className="font-head font-bold text-slate-900">Che vela?</h3>
        </div>
        <div className="space-y-4">
          <SailRow label="Bambino" value={sailKid} onChange={setSailKid} />
          <SailRow label="Adulti" value={sailAdult} onChange={setSailAdult} />
        </div>
      </div>

      <Button type="button" onClick={() => save()} disabled={saving} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 font-head font-semibold" data-testid="verdict-save">
        {saving ? "Salvo..." : "Salva verdetto + vele"}
      </Button>
    </div>
  );
}

function PhraseManager() {
  const [phrases, setPhrases] = useState([]);
  const [current, setCurrent] = useState("");
  const [custom, setCustom] = useState("");

  useEffect(() => {
    api.get("/phrases").then((r) => setPhrases(r.data.phrases)).catch(() => {});
    api.get("/phrase").then((r) => setCurrent(r.data.phrase)).catch(() => {});
  }, []);

  const save = async (phrase) => {
    if (!phrase) return;
    try {
      await api.post("/phrase", { phrase });
      setCurrent(phrase);
      setCustom("");
      toast.success("Frase del giorno aggiornata!");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4" data-testid="phrase-manager">
      <div className="flex items-center gap-2">
        <Quote className="h-5 w-5 text-orange-500" />
        <h2 className="font-head text-xl font-bold text-slate-900">Frase del giorno di Seba</h2>
      </div>
      <div className="bg-slate-900 rounded-2xl px-4 py-3">
        <span className="text-[10px] uppercase tracking-widest font-bold text-orange-400">In diretta ora</span>
        <p className="font-head font-semibold text-white italic mt-0.5" data-testid="phrase-current">"{current || "—"}"</p>
      </div>
      <div>
        <Label>Scegli una frase</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {phrases.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => save(p)}
              data-testid={`phrase-option-${i}`}
              className={`text-sm rounded-full px-3 py-1.5 border transition-colors duration-200 ${
                current === p ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
              }`}
            >
              "{p}"
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="customphrase">Oppure scrivine una tua</Label>
        <div className="flex gap-2 mt-1.5">
          <Input id="customphrase" value={custom} onChange={(e) => setCustom(e.target.value)}
            className="rounded-xl h-11" placeholder="La frase di oggi..." data-testid="phrase-custom" />
          <Button type="button" onClick={() => save(custom)} className="h-11 rounded-xl bg-sky-600 hover:bg-sky-700 font-semibold" data-testid="phrase-save">
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
}

function UpdateForm({ spots, onDone }) {
  const [form, setForm] = useState({
    spot_id: "", title: "", note: "", wind_rating: "good", wind_knots: "", wind_direction: "",
  });
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      setImagePath(data.path);
      toast.success("Foto caricata");
    } catch {
      toast.error("Errore nel caricamento della foto");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.note) {
      toast.error("Titolo e nota sono obbligatori");
      return;
    }
    setSaving(true);
    const spot = spots.find((s) => s.id === form.spot_id);
    try {
      await api.post("/updates", {
        ...form,
        spot_name: spot?.name || "",
        wind_knots: form.wind_knots ? parseFloat(form.wind_knots) : null,
        image_path: imagePath,
      });
      toast.success("Aggiornamento pubblicato!");
      setForm({ spot_id: "", title: "", note: "", wind_rating: "good", wind_knots: "", wind_direction: "" });
      setImagePath("");
      onDone();
    } catch {
      toast.error("Errore nella pubblicazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4" data-testid="update-form">
      <div className="flex items-center gap-2">
        <Wind className="h-5 w-5 text-sky-600" />
        <h2 className="font-head text-xl font-bold text-slate-900">Pubblica un aggiornamento</h2>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Spot</Label>
          <Select value={form.spot_id} onValueChange={(v) => setForm({ ...form, spot_id: v })}>
            <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="update-spot-select">
              <SelectValue placeholder="Seleziona spot" />
            </SelectTrigger>
            <SelectContent>
              {spots.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Condizioni</Label>
          <Select value={form.wind_rating} onValueChange={(v) => setForm({ ...form, wind_rating: v })}>
            <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="update-rating-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RATINGS.map((r) => (
                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="title">Titolo</Label>
        <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="mt-1.5 rounded-xl h-11" placeholder="Es. Ora spinge forte a Torbole!" data-testid="update-title" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="knots">Vento (nodi)</Label>
          <Input id="knots" type="number" value={form.wind_knots} onChange={(e) => setForm({ ...form, wind_knots: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="18" data-testid="update-knots" />
        </div>
        <div>
          <Label htmlFor="dir">Direzione</Label>
          <Input id="dir" value={form.wind_direction} onChange={(e) => setForm({ ...form, wind_direction: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="S-SE (Ora)" data-testid="update-direction" />
        </div>
      </div>

      <div>
        <Label htmlFor="note">Nota</Label>
        <Textarea id="note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="mt-1.5 rounded-xl" rows={3} placeholder="Racconta le condizioni sul campo..." data-testid="update-note" />
      </div>

      <div>
        <Label>Foto (opzionale)</Label>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="update-file-input" />
        {imagePath ? (
          <div className="mt-1.5 relative rounded-xl overflow-hidden border border-slate-200">
            <img src={fileUrl(imagePath)} alt="preview" className="w-full h-40 object-cover" />
            <button type="button" onClick={() => setImagePath("")} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" data-testid="remove-photo">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="mt-1.5 rounded-xl w-full h-11" data-testid="update-upload-btn">
            {uploading ? <>Caricamento...</> : <><Upload className="h-4 w-4 mr-2" /> Carica foto</>}
          </Button>
        )}
      </div>

      <Button type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-sky-600 hover:bg-sky-700 font-head font-semibold" data-testid="update-submit">
        <Plus className="h-4 w-4 mr-2" /> {saving ? "Pubblico..." : "Pubblica aggiornamento"}
      </Button>
    </form>
  );
}

function UpdatesList({ updates, onDone }) {
  const del = async (id) => {
    await api.delete(`/updates/${id}`);
    toast.success("Aggiornamento eliminato");
    onDone();
  };
  if (!updates.length) return null;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
      <h3 className="font-head text-lg font-bold text-slate-900 mb-4">Aggiornamenti pubblicati</h3>
      <div className="space-y-2">
        {updates.map((u) => {
          const meta = ratingMeta(u.wind_rating);
          return (
            <div key={u.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`admin-update-${u.id}`}>
              {u.image_path ? (
                <img src={fileUrl(u.image_path)} alt="" className="h-10 w-10 rounded-lg object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center">
                  <ImageIcon className="h-4 w-4 text-sky-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 truncate">{u.title}</div>
                <div className="text-xs text-slate-400">{u.spot_name} · {meta.label}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => del(u.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full" data-testid={`delete-update-${u.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SpotManager({ spots, onDone }) {
  const [form, setForm] = useState(emptySpot);
  const [editId, setEditId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.latitude === "" || form.longitude === "") {
      toast.error("Nome, latitudine e longitudine obbligatori");
      return;
    }
    const payload = {
      ...form,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
    };
    try {
      if (editId) {
        await api.put(`/spots/${editId}`, payload);
        toast.success("Spot aggiornato");
      } else {
        await api.post("/spots", payload);
        toast.success("Spot aggiunto");
      }
      setForm(emptySpot);
      setEditId(null);
      onDone();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const edit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name, latitude: s.latitude, longitude: s.longitude, description: s.description || "", image_url: s.image_url || "", webcam_url: s.webcam_url || "", windguru_id: s.windguru_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    await api.delete(`/spots/${id}`);
    toast.success("Spot rimosso");
    onDone();
  };

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4" data-testid="spot-form">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-sky-600" />
          <h2 className="font-head text-xl font-bold text-slate-900">
            {editId ? "Modifica spot" : "Aggiungi spot"}
          </h2>
        </div>
        <div>
          <Label htmlFor="sname">Nome</Label>
          <Input id="sname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="Es. Bogliaco" data-testid="spot-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="lat">Latitudine</Label>
            <Input id="lat" type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              className="mt-1.5 rounded-xl h-11" placeholder="45.869" data-testid="spot-lat" />
          </div>
          <div>
            <Label htmlFor="lon">Longitudine</Label>
            <Input id="lon" type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              className="mt-1.5 rounded-xl h-11" placeholder="10.874" data-testid="spot-lon" />
          </div>
        </div>
        <div>
          <Label htmlFor="sdesc">Descrizione</Label>
          <Input id="sdesc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="Note sullo spot" data-testid="spot-desc" />
        </div>
        <div>
          <Label htmlFor="simg">URL immagine (opzionale)</Label>
          <Input id="simg" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="https://..." data-testid="spot-img" />
        </div>
        <div>
          <Label htmlFor="swebcam">URL webcam (opzionale)</Label>
          <Input id="swebcam" value={form.webcam_url} onChange={(e) => setForm({ ...form, webcam_url: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="Link embed webcam o immagine .jpg" data-testid="spot-webcam" />
          <p className="text-[11px] text-slate-400 mt-1">Incolla l'URL di embed della webcam (iframe) o il link diretto a un'immagine (.jpg).</p>
        </div>
        <div>
          <Label htmlFor="swg">ID Windguru (opzionale)</Label>
          <Input id="swg" value={form.windguru_id} onChange={(e) => setForm({ ...form, windguru_id: e.target.value })}
            className="mt-1.5 rounded-xl h-11" placeholder="Es. 599540 (Torbole)" data-testid="spot-windguru" />
          <p className="text-[11px] text-slate-400 mt-1">Numero dello spot su windguru.cz (es. windguru.cz/599540 → 599540). Mostra tabelle e modelli AROME/ICON-CH.</p>
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 font-head font-semibold" data-testid="spot-submit">
            {editId ? "Salva modifiche" : "Aggiungi spot"}
          </Button>
          {editId && (
            <Button type="button" variant="outline" className="h-11 rounded-xl" onClick={() => { setEditId(null); setForm(emptySpot); }} data-testid="spot-cancel">
              Annulla
            </Button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-head text-lg font-bold text-slate-900 mb-4">Spot ({spots.length})</h3>
        <div className="space-y-2">
          {spots.map((s) => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`admin-spot-${s.id}`}>
              <div className="h-9 w-9 rounded-full bg-sky-100 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-sky-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{s.name}</div>
                <div className="text-xs text-slate-400">{s.latitude}, {s.longitude}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => edit(s)} className="rounded-full text-slate-500" data-testid={`edit-spot-${s.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => del(s.id)} className="rounded-full text-red-500 hover:bg-red-50" data-testid={`delete-spot-${s.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
